import {Component, forwardRef, Input} from "@angular/core";
import {SearchAction, SearchActionSelector} from "../../entity/search/search-grid/actions/search-action.component";
import {DataClientError, EntityModel, SearchModel} from "@fedeltapointofsale/data-client";
import {DeleteSearchActionComponent} from "./delete-search-action.component";
import {FUIAlertAction, FUIAlertActions, FUIAlertService} from "@fedeltapointofsale/ui";
import {AnalyticEvent} from "../../../analytics/analytic-event";
import {AnalyticsService} from "../../../analytics/analytics.service";
import {firstValueFrom} from "rxjs";

/**
 * SearchAction that supports discontinue/recontinue of entities in the search grid
 */
@Component({
    selector: "wbo-discontinue-search-action",
    template: "",
    providers: [
        {provide: SearchActionSelector, useExisting: forwardRef(() => DiscontinueSearchActionComponent), multi: true}
    ]

})
export class DiscontinueSearchActionComponent implements SearchAction {
    static readonly DISCONTINUE_NAME: string = "wbo.common.button.discontinue";
    static readonly DISCONTINUE_ICON: string = "ban";
    static readonly RECONTINUE_NAME: string = "wbo.common.button.recontinue";
    static readonly RECONTINUE_ICON: string = "restart";
    static message: string = "wbo.search.prompt.discontinue.message";
    static title: string = "wbo.search.prompt.discontinue.heading";

    @Input() discontinueMessageKey: string = DiscontinueSearchActionComponent.message;
    @Input() discontinueTitleKey: string = DiscontinueSearchActionComponent.title;

    @Input() discontinueName: string = DiscontinueSearchActionComponent.DISCONTINUE_NAME;
    @Input() recontinueName: string = DiscontinueSearchActionComponent.RECONTINUE_NAME;

    @Input() allowRecontinue: boolean = true;

    @Input() availableCallback: (data: any) => boolean;

    private available: boolean = false;

    constructor(private alertService: FUIAlertService,
                private entityModel: EntityModel<any>,
                private searchModel: SearchModel<any>,
                private analyticsService: AnalyticsService) {
        this.entityModel.canDiscontinueEntity().then((canDiscontinue: boolean) => this.available = canDiscontinue);
    }

    /**
     *
     * @returns {number}
     */
    getImportance(): number {
        return DeleteSearchActionComponent.IMPORTANCE;
    }

    /**
     *
     * @param data
     * @returns {string}
     */
    getIcon(data: any): string {
        if (this.searchModel.isDiscontinued(data)) {
            return DiscontinueSearchActionComponent.RECONTINUE_ICON;
        }

        return DiscontinueSearchActionComponent.DISCONTINUE_ICON;
    }

    getIconClass(data: any): string {
        return "";
    }

    getIconSize(data: any): number {
        return 17;
    }

    /**
     *
     * @param data
     * @returns {string}
     */
    getName(data: any): string {
        if (this.searchModel.isDiscontinued(data)) {
            return this.recontinueName;
        }

        return this.discontinueName;
    }

    /**
     *
     * @param data
     * @returns {Promise<TResult2|TResult1>}
     */
    async doAction(data: any): Promise<void> {
        if (this.searchModel.isDiscontinued(data)) {
            await this.entityModel.recontinueEntity(this.searchModel.getIdentifier(data));
            this.analyticsService.trackEvent(AnalyticEvent.SEARCH_RECONTINUE_ROW, this.searchModel);
            this.searchModel.setDiscontinued(data, false);
            return;
        }

        const action: FUIAlertAction = await firstValueFrom(this.alertService.showConfirmationOkCancel(this.discontinueTitleKey, this.discontinueMessageKey));
        if (action === FUIAlertActions.OK_ACTION) {
            this.analyticsService.trackEvent(AnalyticEvent.SEARCH_DISCONTINUE_ROW, this.searchModel);
            try {
                await this.entityModel.discontinueEntity(this.searchModel.getIdentifier(data));
                this.searchModel.setDiscontinued(data, true);
            } catch (error) {
                if (error instanceof DataClientError) {
                    if (await error.validationMessages) {
                        this.alertService.showError("wbo.search.error.couldNotDiscontinueErrorsEntity.heading",
                            "wbo.search.error.couldNotDiscontinueErrorsEntity.message").subscribe(() => {
                        });
                    }
                }
                throw (error);
            }
        }
    }

    isAvailable(data: any): boolean {
        if (this.searchModel.isDiscontinued(data)) {
            return this.allowRecontinue;
        }
        if (!this.available) {
            return false;
        }
        if (this.availableCallback) {
            return this.availableCallback(data);
        }
        return true;
    }
}
